// The STANDING per-plugin permissions panel for Settings (spec 7.6). For every
// installed plugin it lists each declared capability with its Required/Optional
// kind, reason, scope, domains, and live granted state, plus a computed access
// badge and a Grant/Revoke toggle per line. It is a pure projection of broker
// state: it re-renders on broker.onDidChange, and grant/revoke go straight to the
// broker, whose onDidChange is already forwarded to the running worker (setup.ts),
// so a revoke blocks the capability immediately AND the plugin reacts live.
//
// This is the detailed, audit-grade companion to the compact sidebar PluginsPanel;
// the two share the broker patterns, not a component, by design.

import { useEffect, useState, type ReactElement } from 'react';
import type { PermissionState } from './permissionBroker';
import type { PluginSystem } from './setup';
import { computeAccessBadge, CAPABILITY_LABELS } from './accessBadge';

export interface PluginsSettingsProps {
  system: PluginSystem;
}

const STATE_LABELS: Record<PermissionState, string> = {
  granted: 'Granted',
  denied: 'Not granted',
  prompt: 'Not granted',
};

export function PluginsSettings({ system }: PluginsSettingsProps): ReactElement {
  // Re-render whenever ANY grant changes so rows and badges stay live. The same
  // broker.onDidChange is also wired in setup.ts to notify the running worker, so
  // a revoke here both updates this UI and reaches the plugin.
  const [, bump] = useState(0);
  useEffect(() => system.broker.onDidChange(() => bump((n) => n + 1)), [system]);

  const plugins = system.host.list();

  if (plugins.length === 0) {
    return (
      <div className="mp-settings__sections">
        <section className="mp-settings__section">
          <p className="mp-settings__row-help">No plugins installed.</p>
        </section>
      </div>
    );
  }

  return (
    <div className="mp-settings__sections">
      {plugins.map((p) => {
        const id = p.manifest.id;
        const perms = p.manifest.permissions ?? [];
        const granted = perms
          .map((perm) => perm.capability)
          .filter((cap) => system.broker.query(id, cap) === 'granted');
        const badge = computeAccessBadge(granted);

        return (
          <section key={id} className="mp-settings__section mp-plugin-perms">
            <div className="mp-settings__section-head mp-plugin-perms__head">
              <div className="mp-plugin-perms__identity">
                <h4>{p.manifest.name}</h4>
                {p.manifest.description && <p>{p.manifest.description}</p>}
              </div>
              <span
                className="mp-plugin-perms__badge"
                data-level={badge.level}
                aria-label={`Access level: ${badge.label}. ${badge.detail}.`}
              >
                <span className="mp-plugin-perms__badge-label">{badge.label}</span>
                <span className="mp-plugin-perms__badge-detail" aria-hidden>
                  {badge.detail}
                </span>
              </span>
            </div>

            {perms.length === 0 ? (
              <p className="mp-settings__row-help">
                This plugin requests no native permissions.
              </p>
            ) : (
              <ul className="mp-plugin-perms__list" role="list">
                {perms.map((perm) => {
                  const cap = perm.capability;
                  const state = system.broker.query(id, cap);
                  const isGranted = state === 'granted';
                  const capLabel = CAPABILITY_LABELS[cap];
                  return (
                    <li key={cap} className="mp-plugin-perms__row" data-state={state}>
                      <div className="mp-plugin-perms__info">
                        <span className="mp-plugin-perms__cap">
                          <span className="mp-plugin-perms__cap-id">{cap}</span>
                          <span className="mp-plugin-perms__cap-label">{capLabel}</span>
                          <span
                            className="mp-plugin-perms__tag"
                            data-kind={perm.optional ? 'optional' : 'mandatory'}
                          >
                            {perm.optional ? 'Optional' : 'Required'}
                          </span>
                        </span>
                        <p className="mp-plugin-perms__reason">{perm.reason}</p>
                        {perm.scope && (
                          <p className="mp-plugin-perms__scope">
                            Scope: <strong>{perm.scope}</strong>
                          </p>
                        )}
                        {perm.domains && perm.domains.length > 0 && (
                          <p className="mp-plugin-perms__domains">
                            Domains: <strong>{perm.domains.join(', ')}</strong>
                          </p>
                        )}
                      </div>
                      <div className="mp-plugin-perms__control">
                        <span className="mp-plugin-perms__state" data-state={state}>
                          {STATE_LABELS[state]}
                        </span>
                        {isGranted ? (
                          <button
                            type="button"
                            className="mp-btn mp-btn--danger"
                            aria-label={`Revoke ${capLabel} for ${p.manifest.name}`}
                            onClick={() => system.broker.revoke(id, cap)}
                          >
                            Revoke
                          </button>
                        ) : (
                          <button
                            type="button"
                            className="mp-btn"
                            aria-label={`Grant ${capLabel} for ${p.manifest.name}`}
                            onClick={() => system.broker.grant(id, cap)}
                          >
                            Grant
                          </button>
                        )}
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </section>
        );
      })}
    </div>
  );
}
